/* eslint-disable no-useless-assignment */
import { Checkbox, Combobox, SimpleGrid, useCombobox } from "@mantine/core";
import type { TieredPriceProductId } from "@project/common";
import { TIERED_PRODUCT_IDS } from "@project/common";
import type { FC } from "react";
import { useIntl } from "react-intl";
import { useDisclosure } from "@mantine/hooks";
import { DropdownTrigger } from "./DropdownTrigger.js";
import { IconGraph } from "@tabler/icons-react";
import { useProductNames } from "../../components/hooks/productLabels.js";

export interface DetailedViewSelectorProps {
  metricsInDetailedView: TieredPriceProductId[];
  addToDetailedView: (...items: TieredPriceProductId[]) => void;
  removeFromDetailedView: (...indices: number[]) => void;
  clearDetailedView: () => void;
}

export const DetailedViewSelector: FC<DetailedViewSelectorProps> = ({
  metricsInDetailedView,
  addToDetailedView,
  removeFromDetailedView,
  clearDetailedView,
}) => {
  const intl = useIntl();
  const getName = useProductNames();
  const [opened, { toggle, close }] = useDisclosure(false);
  const combobox = useCombobox({ opened });

  const count = metricsInDetailedView.length;
  const [metric] = metricsInDetailedView;

  const allMetrics = intl.formatMessage({
    defaultMessage: "All metrics",
    id: "detailed.allMetrics",
  });

  const togglePresence = (productId: TieredPriceProductId) => () => {
    const index = metricsInDetailedView.indexOf(productId);
    if (index < 0) {
      addToDetailedView(productId);
    }
    else {
      removeFromDetailedView(index);
    }
  };

  let triggerLabel = "";
  if (count === 0) {
    triggerLabel = allMetrics;
  }
  else if (count === 1 && metric) {
    triggerLabel = getName(metric);
  }
  else {
    triggerLabel = intl.formatMessage({
      defaultMessage: "{count} selected",
      id: "detail.countSelected",
    }, { count });
  }

  return (
    <Combobox
      width="auto"
      store={combobox}
      onDismiss={close}
    >
      <Combobox.Target>
        <DropdownTrigger
          opened={opened}
          toggle={toggle}
          leftSection={<IconGraph />}
          text={triggerLabel}
        />
      </Combobox.Target>

      <Combobox.Dropdown>
        <SimpleGrid cols={2} p="sm">
          <Checkbox
            value="all metrics"
            label={allMetrics}
            checked={count === 0}
            onChange={(e) => {
              const { checked } = e.target;
              if (checked) {
                clearDetailedView();
              }
            }}
          />
          {TIERED_PRODUCT_IDS.map(option => (
            <Checkbox
              key={option}
              value={option}
              onChange={togglePresence(option)}
              checked={metricsInDetailedView.includes(option)}
              label={getName(option)}
            />
          ))}
        </SimpleGrid>
      </Combobox.Dropdown>
    </Combobox>
  );
};
